/**
 * Debug Logging Utility
 *
 * Provides environment-gated logging that only runs in development.
 * Production builds will completely remove these logs via dead code elimination.
 *
 * Usage:
 *   import { debugLog } from '../utils/debugLog';
 *   debugLog('[Component] - Debug message:', data);
 *
 * @module utils/debugLog
 */

const DEBUG = process.env.NODE_ENV === 'development';

/**
 * Console log that only executes in development environment
 *
 * @param {...any} args - Arguments to pass to console.log
 *
 * @example
 * debugLog('[Auth] - User signed in:', { id: user.id });
 * // Development: logs to console
 * // Production: completely removed from bundle
 */
export const debugLog = (...args) => {
  if (DEBUG) {
    console.log(...args);
  }
};

/**
 * Console warn that only executes in development environment
 *
 * @param {...any} args - Arguments to pass to console.warn
 *
 * @example
 * debugWarn('[API] - Slow response time:', responseTime);
 */
export const debugWarn = (...args) => {
  if (DEBUG) {
    console.warn(...args);
  }
};

/**
 * Console error that only executes in development environment
 * Note: For production errors that need monitoring, use regular console.error
 *
 * @param {...any} args - Arguments to pass to console.error
 *
 * @example
 * debugError('[Component] - Render error:', error);
 */
export const debugError = (...args) => {
  if (DEBUG) {
    console.error(...args);
  }
};

/**
 * Console table that only executes in development environment
 * Useful for debugging arrays and objects
 *
 * @param {*} data - Data to display in table format
 * @param {string[]} columns - Optional columns to display
 *
 * @example
 * debugTable(users, ['id', 'name', 'email']);
 */
export const debugTable = (data, columns) => {
  if (DEBUG) {
    console.table(data, columns);
  }
};

/**
 * Console group that only executes in development environment
 * Useful for grouping related log messages
 *
 * @param {string} label - Group label
 *
 * @example
 * debugGroup('API Call');
 * debugLog('Request:', request);
 * debugLog('Response:', response);
 * debugGroupEnd();
 */
export const debugGroup = (label) => {
  if (DEBUG) {
    console.group(label);
  }
};

/**
 * Console groupEnd that only executes in development environment
 */
export const debugGroupEnd = () => {
  if (DEBUG) {
    console.groupEnd();
  }
};

/**
 * Console time that only executes in development environment
 * Useful for performance debugging
 *
 * @param {string} label - Timer label
 *
 * @example
 * debugTime('API Call');
 * await fetchData();
 * debugTimeEnd('API Call');
 */
export const debugTime = (label) => {
  if (DEBUG) {
    console.time(label);
  }
};

/**
 * Console timeEnd that only executes in development environment
 *
 * @param {string} label - Timer label
 */
export const debugTimeEnd = (label) => {
  if (DEBUG) {
    console.timeEnd(label);
  }
};

// Default export for convenience
export default {
  log: debugLog,
  warn: debugWarn,
  error: debugError,
  table: debugTable,
  group: debugGroup,
  groupEnd: debugGroupEnd,
  time: debugTime,
  timeEnd: debugTimeEnd,
};
